import * as THREE from 'three';
import { proxied } from '../services/assets';
import { cityTime } from './cityTime';

// THE STAGE IS A BILLBOARD (Khari, 2026-10-23, watching Malaki's set: "we should
// place titling and graphics behind them… Is it possible to create a slideshow
// or other inexpensive display of the current artist's media. We could include
// their name in large bold letters while some other animation displays in
// parts or along the entire screen area behind them.").
//
// One canvas, drawn as the LED wall's picture while a set is on (or about to
// be): the performer's NAME across the top, the song under it, the cover of
// what is playing on one side and a slideshow of the rest of their set (and
// their own picture) on the other, over a blurred wash of the cover with light
// sweeping through it, a level meter along the bottom and the LED pixel grid
// on top. The performer stands in front of the middle, so the middle is light
// and the words sit above their head.
//
// Cheap on purpose: ~15 redraws a second, and only while someone is close
// enough to see it; images come through the API's asset proxy (a canvas drawn
// with a cross-origin image cannot become a texture).

const W = 1280; const H = 376;   // the wall's own shape (5.8 × 1.7)
const FPS = 15;

// Shared with the venues' wall screens (city/venueScreen), so a cover that is on
// the plaza wall and a club wall is fetched and decoded once.
const images = new Map();          // url → { img, ok, blur }
export function imageFor(url) {
  if (!url) return null;
  let e = images.get(url);
  if (!e) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    e = { img, ok: false, blur: null };
    img.onload = () => {
      e.ok = true;
      // A blurred copy for the wash: drawn tiny, then stretched back up.
      try {
        const c = document.createElement('canvas'); c.width = 48; c.height = 14;
        c.getContext('2d').drawImage(img, 0, 0, 48, 14);
        e.blur = c;
      } catch { /* the wash falls back to colour */ }
    };
    img.onerror = () => { e.ok = false; };
    img.src = proxied(url);
    images.set(url, e);
    if (images.size > 60) images.delete(images.keys().next().value);
  }
  return e.ok ? e : null;
}

/** Cover the box with the image (centre-cropped), zoomed by `z` (Ken Burns). */
export function drawCover(g, e, x, y, w, h, z = 1, alpha = 1) {
  const iw = e.img.naturalWidth || 1; const ih = e.img.naturalHeight || 1;
  const s = Math.max(w / iw, h / ih) * z;
  const dw = iw * s; const dh = ih * s;
  g.save();
  g.globalAlpha = alpha;
  g.beginPath(); g.rect(x, y, w, h); g.clip();
  g.drawImage(e.img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  g.restore();
}

export function fitFont(g, text, maxW, maxPx, minPx, weight = 900) {
  let px = maxPx;
  for (; px > minPx; px -= 4) {
    g.font = `${weight} ${px}px system-ui, -apple-system, 'Segoe UI', sans-serif`;
    if (g.measureText(text).width <= maxW) break;
  }
  return px;
}

export function makeStageScreen() {
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const g = canvas.getContext('2d');
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  let last = 0;

  /**
   * Draw one frame for `show` at server time `t` (ms). `show`: the set from the
   * city ({ name, t0, t1, tracks: [{ title, art, at, durMs }] }); `face`: their
   * picture. Returns false when there is nothing to show (the wall's own wash).
   */
  function draw(show, t, face) {
    if (!show) return false;
    const now = performance.now();
    if (now - last < 1000 / FPS) return true;
    last = now;
    const s = (t - show.t0) / 1000;               // seconds into the set (negative before)
    const tracks = show.tracks || [];
    let i = -1;
    for (let k = 0; k < tracks.length; k++) if (t >= show.t0 + tracks[k].at) i = k;
    const cur = tracks[Math.max(0, i)] || null;
    const playing = cur && i >= 0 && t < show.t0 + cur.at + cur.durMs;
    const into = cur && i >= 0 ? (t - show.t0 - cur.at) / Math.max(1, cur.durMs) : 0;
    const arts = [...new Set(tracks.map((x) => x.art).filter(Boolean))];
    const curArt = imageFor(cur?.art) || imageFor(arts[0]);
    const pulse = 0.5 + 0.5 * Math.sin(now / 1000 * Math.PI * 1.6);

    // The wash: their cover, blurred and dark, or a warm dusk when it has none.
    g.globalCompositeOperation = 'source-over';
    g.fillStyle = '#07040a'; g.fillRect(0, 0, W, H);
    if (curArt?.blur) {
      g.globalAlpha = 0.55; g.imageSmoothingEnabled = true;
      g.drawImage(curArt.blur, -20, -20, W + 40, H + 40);
      g.globalAlpha = 1;
    } else {
      const sun = g.createRadialGradient(W / 2, H * 1.05, 10, W / 2, H * 1.05, W * 0.62);
      sun.addColorStop(0, 'rgba(255,214,150,1)'); sun.addColorStop(0.3, 'rgba(255,128,64,0.85)');
      sun.addColorStop(0.65, 'rgba(170,40,90,0.5)'); sun.addColorStop(1, 'rgba(30,8,40,0)');
      g.fillStyle = sun; g.fillRect(0, 0, W, H);
    }
    g.fillStyle = 'rgba(8,4,12,0.35)'; g.fillRect(0, 0, W, H);

    // Light sweeping through: shafts that sway with the set.
    g.globalCompositeOperation = 'lighter';
    for (let k = 0; k < 9; k++) {
      const x = W * (0.06 + k * 0.11) + Math.sin(now / 1700 + k * 0.9) * 40;
      const lean = Math.sin(now / 2300 + k) * 60;
      const shaft = g.createLinearGradient(0, 0, 0, H);
      shaft.addColorStop(0, `rgba(255,190,140,${0.1 + 0.08 * pulse})`); shaft.addColorStop(1, 'rgba(255,150,120,0)');
      g.fillStyle = shaft;
      g.beginPath(); g.moveTo(x - 10, 0); g.lineTo(x + 10, 0); g.lineTo(x + 60 + lean, H); g.lineTo(x - 60 + lean, H); g.closePath(); g.fill();
    }
    g.globalCompositeOperation = 'source-over';

    // Left: what is playing, slowly pushing in.
    const box = 222; const by = H - box - 34;
    if (curArt) {
      drawCover(g, curArt, 48, by, box, box, 1.02 + 0.06 * Math.min(1, Math.max(0, into)));
      g.strokeStyle = 'rgba(255,220,180,0.55)'; g.lineWidth = 3; g.strokeRect(48, by, box, box);
    }
    // Right: the rest of their set, and them — a slideshow, crossfading.
    const deck = [...arts.filter((a) => a !== cur?.art), face].filter(Boolean);
    if (deck.length) {
      const per = 6;                                  // seconds a picture
      const k = Math.floor(now / 1000 / per);
      const f = ((now / 1000) % per) / per;
      const a = imageFor(deck[k % deck.length]); const b = imageFor(deck[(k + 1) % deck.length]);
      const x = W - 48 - box;
      if (a) drawCover(g, a, x, by, box, box, 1.03 + 0.05 * f);
      if (b && f > 0.85) drawCover(g, b, x, by, box, box, 1.03, (f - 0.85) / 0.15);
      if (a || b) { g.strokeStyle = 'rgba(255,220,180,0.55)'; g.lineWidth = 3; g.strokeRect(x, by, box, box); }
    }

    // The words, above their head.
    g.textAlign = 'center'; g.textBaseline = 'alphabetic';
    const before = s < 0;
    const label = before ? 'UP NEXT ON THE PLAZA STAGE' : 'LIVE ON THE PLAZA STAGE';
    g.font = '800 22px system-ui, -apple-system, sans-serif';
    g.fillStyle = `rgba(255,200,150,${0.75 + 0.25 * pulse})`;
    g.fillText(label.split('').join(String.fromCharCode(8202)), W / 2, 40);
    const name = String(show.name || '').toUpperCase();
    const px = fitFont(g, name, W - 120, 118, 48);
    g.shadowColor = 'rgba(255,140,90,0.9)'; g.shadowBlur = 26;
    g.fillStyle = '#fff6ea';
    g.fillText(name, W / 2, 40 + px * 0.95);
    g.shadowBlur = 0;
    const sub = before
      ? (s > -60 ? `in ${Math.ceil(-s)}s` : `at ${cityTime(show.t0)}`)
      : playing ? `“${cur.title}”` : (i >= 0 && i < tracks.length - 1 ? `next: “${tracks[i + 1].title}”` : '');
    if (sub) {
      fitFont(g, sub, W * 0.5, 34, 20, 700);
      g.fillStyle = '#ffd9b0';
      g.fillText(sub, W / 2, 40 + px * 0.95 + 46);
    }

    // A level meter along the bottom (no analysis: it breathes with the set).
    if (playing) {
      const bars = 64; const bw = W / bars;
      for (let k = 0; k < bars; k++) {
        const v = 0.35 + 0.65 * Math.abs(Math.sin(now / 260 + k * 0.7) * Math.cos(now / 410 + k * 0.33));
        const bh = 6 + v * 26;
        g.fillStyle = `rgba(255,${150 + Math.round(70 * v)},${110 + Math.round(40 * v)},0.55)`;
        g.fillRect(k * bw + 2, H - bh - 4, bw - 4, bh);
      }
    }

    // The LED wall's pixel grid, over everything.
    g.fillStyle = 'rgba(0,0,0,0.32)';
    for (let x = 0; x < W; x += 4) g.fillRect(x, 0, 1, H);
    for (let y = 0; y < H; y += 4) g.fillRect(0, y, W, 1);

    texture.needsUpdate = true;
    return true;
  }

  return { canvas, texture, draw, dispose: () => texture.dispose() };
}
